import type { AdminAiScanFindingSummaryProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import { ADMIN_AI_SCAN_SEVERITY_LABELS, ADMIN_AI_SCAN_TONE_CLASSES } from "../../utils/admin-ai-scans.constants";
import {
  getAdminAiScanFindingExcerpt,
  getAdminAiScanFindingScore,
  getAdminAiScanFindingSeverity,
  getAdminAiScanFindingTitle,
  getAdminAiScanText,
} from "../../utils/admin-ai-scans.utils";
import { twMerge } from "tailwind-merge";

// Keep severity, score, explanation and origin in stable columns in both the tree and flat findings list.
const AdminAiScanFindingSummary = ({ finding, source }: AdminAiScanFindingSummaryProps) => {
  const severity = getAdminAiScanFindingSeverity(finding);
  const score = getAdminAiScanFindingScore(finding);
  const category = getAdminAiScanText(finding.category).replace(/[-_]/g, " ");
  const location = getAdminAiScanText(
    finding.routeOrMethod || finding.routeMethod || finding.filePath || finding.symbol,
  );

  return (
    <span className={adminAiScansStyles.findingGrid}>
      <span className="col-start-1 row-start-1 grid min-w-0 gap-1.5">
        <span className={twMerge(adminAiScansStyles.severityBadge, ADMIN_AI_SCAN_TONE_CLASSES[severity])}>
          {ADMIN_AI_SCAN_SEVERITY_LABELS[severity]}
        </span>
        {category && (
          <span className="break-words text-center text-[10px] font-bold uppercase leading-4 text-slate-600 dark:text-gray-300">
            {category}
          </span>
        )}
      </span>
      <span
        className="col-start-1 row-start-2 text-center text-sm font-bold tabular-nums leading-7 text-slate-900 dark:text-white md:col-start-2 md:row-start-1 md:text-right"
        aria-label={`Risk score: ${score === null ? "unspecified" : score}`}
      >
        {score === null ? "—" : score.toFixed(2)}
      </span>
      <span className="col-start-2 row-span-2 row-start-1 min-w-0 md:col-start-3 md:row-span-1">
        <span className="block break-words text-sm font-bold leading-6 text-slate-900 dark:text-white">
          {getAdminAiScanFindingTitle(finding)}
        </span>
        <span className="mt-1 line-clamp-5 whitespace-pre-wrap break-words text-sm font-normal leading-6 text-slate-600 dark:text-gray-300">
          {getAdminAiScanFindingExcerpt(finding)}
        </span>
      </span>
      <span className="col-start-2 row-start-3 min-w-0 break-words text-xs font-normal leading-5 text-slate-500 dark:text-gray-400 md:col-start-3 md:row-start-2 xl:col-start-4 xl:row-start-1">
        {source && (
          <span className="block">
            {source.moduleLabel} / {source.businessObjectLabel}
          </span>
        )}
        {location && <span className="block">{location}</span>}
        <span className="mt-1 block font-mono text-[10px]">{getAdminAiScanText(finding.id)}</span>
      </span>
    </span>
  );
};

export default AdminAiScanFindingSummary;
